import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../utils/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Loader2, Search, Plus, X, Sparkles, ArrowLeft, FolderKanban, Calculator } from 'lucide-react';
import { toast } from 'sonner';

/**
 * AICalcPickerModal
 * Opens from the 3 dashboard quick-calc buttons.
 * Step A: pick existing project (search/list) OR "+ Nytt projekt"
 * Step B (if new): minimal create form (name, description, location)
 * → Navigates to /dashboard/projects/<id>?tab=<tab>&autorun=1
 */

const CONFIG = {
  material: {
    label: 'Beräkna Material',
    tab: 'material-plan',
    title: 'AI Materialberäkning',
    desc: 'Välj projektet du vill beräkna materialåtgång och inköpslista för.',
  },
  labor: {
    label: 'Beräkna Arbete',
    tab: 'budget',
    title: 'AI Arbetskostnad',
    desc: 'Välj projektet — AI:n räknar ut arbetstimmar, lön och totalkostnad.',
  },
  time: {
    label: 'Beräkna Tid',
    tab: 'ai-estimate',
    title: 'AI Tidsplan',
    desc: 'Välj projektet — AI:n beräknar arbetsdagar, milstolpar och risker.',
  },
};

const AICalcPickerModal = ({ kind, onClose, onWithoutProject }) => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [view, setView] = useState('pick'); // 'pick' | 'create'
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', location: '' });

  const cfg = CONFIG[kind] || CONFIG.time;

  useEffect(() => {
    let alive = true;
    api.getProjects()
      .then(res => { if (alive) setProjects(res.data || []); })
      .catch(() => toast.error('Kunde inte hämta projekt'))
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const handlePick = (projectId) => {
    navigate(`/dashboard/projects/${projectId}?tab=${cfg.tab}&autorun=1`);
    onClose();
  };

  const handleCreate = async () => {
    if (!form.name.trim()) {
      toast.error('Ange ett projektnamn');
      return;
    }
    setCreating(true);
    try {
      const res = await api.createProject({
        name: form.name,
        description: form.description || '',
        location: form.location || '',
        status: 'active',
      });
      const newId = res.data?.id;
      if (newId) {
        toast.success('Projekt skapat — AI-beräkning startar');
        navigate(`/dashboard/projects/${newId}?tab=${cfg.tab}&autorun=1`);
        onClose();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte skapa projekt');
    } finally {
      setCreating(false);
    }
  };

  const filtered = projects.filter(p =>
    !search.trim() || (p.name || '').toLowerCase().includes(search.trim().toLowerCase())
  );

  return (
    <div
      className="fixed inset-0 z-[120] bg-black/60 flex items-center justify-center px-4 py-6 overflow-y-auto"
      onClick={onClose}
      data-testid="ai-calc-picker-modal"
    >
      <div
        className="max-w-lg w-full bg-card rounded-2xl border border-border shadow-2xl overflow-hidden my-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-[#1A1A1A] text-white px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-[#E8613A] rounded-lg flex items-center justify-center">
              <Sparkles size={16} />
            </div>
            <div>
              <h2 className="text-base font-semibold">{cfg.title}</h2>
              <p className="text-xs text-white/60 mt-0.5">{view === 'create' ? 'Skapa nytt projekt' : cfg.desc}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-white/10 rounded" aria-label="Stäng" data-testid="ai-calc-close-btn">
            <X size={16} />
          </button>
        </div>

        {view === 'pick' && (
          <>
            <div className="px-6 pt-5 pb-3 space-y-3">
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Sök projekt..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9 h-10 rounded-full text-sm"
                  data-testid="ai-calc-search-input"
                />
              </div>
              <button
                onClick={() => setView('create')}
                className="w-full flex items-center gap-3 bg-[#E8613A]/5 hover:bg-[#E8613A]/10 border-2 border-dashed border-[#E8613A]/30 rounded-xl px-4 py-3 transition-colors"
                data-testid="ai-calc-new-project-btn"
              >
                <div className="w-8 h-8 bg-[#E8613A] rounded-lg flex items-center justify-center text-white">
                  <Plus size={14} />
                </div>
                <span className="text-sm font-medium text-foreground">Skapa nytt projekt</span>
              </button>
              {onWithoutProject && (
                <button
                  onClick={() => { onWithoutProject(kind); onClose(); }}
                  className="w-full flex items-center gap-3 bg-[#1A1A1A] hover:bg-[#2A2A2A] text-white rounded-xl px-4 py-3 transition-colors"
                  data-testid="ai-calc-without-project-btn"
                >
                  <div className="w-8 h-8 bg-[#E8613A] rounded-lg flex items-center justify-center text-white">
                    <Calculator size={14} />
                  </div>
                  <div className="flex-1 text-left">
                    <p className="text-sm font-medium">Beräkna utan projekt</p>
                    <p className="text-[11px] text-white/60">Snabb AI-totalberäkning — spara i Snabb-arkivet</p>
                  </div>
                </button>
              )}
            </div>

            <div className="max-h-[50vh] overflow-y-auto px-6 pb-4">
              {loading ? (
                <div className="flex items-center justify-center py-10">
                  <Loader2 className="w-5 h-5 text-[#E8613A] animate-spin" />
                </div>
              ) : filtered.length === 0 ? (
                <div className="text-center py-10 text-sm text-muted-foreground">
                  {search ? 'Inga projekt matchar sökningen.' : 'Inga befintliga projekt — skapa ditt första ovan.'}
                </div>
              ) : (
                <div className="space-y-1.5">
                  {filtered.map(p => (
                    <button
                      key={p.id}
                      onClick={() => handlePick(p.id)}
                      className="w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-muted transition-colors group"
                      data-testid={`ai-calc-pick-${p.id}`}
                    >
                      <div className="w-9 h-9 bg-muted rounded-lg flex items-center justify-center group-hover:bg-[#E8613A]/10">
                        <FolderKanban size={14} className="text-muted-foreground group-hover:text-[#E8613A]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">{p.name}</p>
                        <p className="text-xs text-muted-foreground truncate">{p.location || p.description || '—'}</p>
                      </div>
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground uppercase tracking-wider font-medium">
                        {p.status || 'aktiv'}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {view === 'create' && (
          <div className="px-6 py-5 space-y-4">
            <div>
              <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Projektnamn *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="ex. Villa Solrosvägen 12"
                className="mt-1.5"
                data-testid="ai-calc-create-name"
                autoFocus
              />
            </div>
            <div>
              <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Plats / adress</Label>
              <Input
                value={form.location}
                onChange={(e) => setForm(f => ({ ...f, location: e.target.value }))}
                placeholder="ex. Skövde"
                className="mt-1.5"
                data-testid="ai-calc-create-location"
              />
            </div>
            <div>
              <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Beskrivning</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Kort beskrivning av arbetet (badrumsrenovering, markarbeten 200 kvm...)"
                rows={3}
                className="mt-1.5"
                data-testid="ai-calc-create-desc"
              />
            </div>
            <div className="flex items-center justify-between pt-2 gap-3">
              <button
                onClick={() => setView('pick')}
                className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1.5"
                data-testid="ai-calc-back-btn"
              >
                <ArrowLeft size={12} /> Tillbaka
              </button>
              <Button
                onClick={handleCreate}
                disabled={creating || !form.name.trim()}
                className="bg-[#E8613A] hover:bg-[#D4532F] text-white h-10 px-5 rounded-full"
                data-testid="ai-calc-create-submit"
              >
                {creating ? (
                  <><Loader2 size={14} className="mr-2 animate-spin" /> Skapar...</>
                ) : (
                  <><Sparkles size={14} className="mr-2" /> Skapa & starta AI</>
                )}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AICalcPickerModal;
